import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { BaseMap, DEFAULT_CENTER, Dot, FitBounds, Line } from "@/lib/mapProvider";

const ReviewMap = ({ r }) => {
  const pts = r.path.map((p) => [p[0], p[1]]);
  const pickup = [r.pickup.lat, r.pickup.lng];
  return (
    <div className="overflow-hidden rounded-xl border border-white/10" data-testid={`offapp-map-${r.id}`}>
      <BaseMap center={pickup || DEFAULT_CENTER} zoom={14} className="h-64 w-full">
        <Dot center={pickup} radius={10} fill="#d4af37" stroke="#0b0b0d" strokeWeight={2} title="Pickup" />
        {pts.length > 1 && <Line positions={pts} color="#ef4444" weight={4} />}
        {pts.length > 0 && <Dot center={pts[pts.length - 1]} radius={7} fill="#ef4444" stroke="#0b0b0d" strokeWeight={2} title="Last driver position" />}
        <FitBounds points={[pickup, ...pts]} fitKey={`${r.id}-${pts.length}`} />
      </BaseMap>
    </div>
  );
};

export const CancelReviews = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/cancel-reviews").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <div className="space-y-6" data-testid="cancel-reviews">
      <div className="lux-card p-4 sm:p-6" data-testid="termination-queue">
        <p className="lux-eyebrow mb-3">Termination pending · 3+ cancellations after arriving this month</p>
        {!d.termination_pending.length && <p className="text-sm text-zinc-500" data-testid="termination-empty">No drivers waiting for review.</p>}
        {d.termination_pending.map((t) => (
          <div key={t.driver_id} data-testid={`termination-row-${t.driver_id}`} className="flex flex-wrap items-center justify-between gap-3 border-t border-white/5 py-3 first:border-0">
            <div><p className="text-amber-100">{t.name} <span className="text-xs text-zinc-500">{t.driver_code}</span></p>
              <p className="text-xs text-zinc-500">{t.arrival_cancels_this_month} cancellations after arriving this month · flagged {fmtDate(t.termination_pending_at)}</p></div>
            <div className="flex gap-2">
              <button data-testid={`termination-approve-${t.driver_id}`} onClick={() => act(() => api.post(`/admin/drivers/${t.driver_id}/termination`, { action: "approve" }), "Contract terminated")} className="btn-danger !px-3 !py-1.5 !text-xs">Approve termination</button>
              <button data-testid={`termination-dismiss-${t.driver_id}`} onClick={() => act(() => api.post(`/admin/drivers/${t.driver_id}/termination`, { action: "dismiss" }), "Review dismissed")} className="btn-outline-gold !px-3 !py-1.5 !text-xs">Dismiss</button>
            </div>
          </div>
        ))}
      </div>
      <div className="lux-card space-y-4 p-4 sm:p-6" data-testid="offapp-queue">
        <p className="lux-eyebrow">Possible off-app rides · driver GPS for 30 min after a customer cancelled at pickup</p>
        {!d.offapp_reviews.length && <p className="text-sm text-zinc-500" data-testid="offapp-empty">Nothing to review.</p>}
        {d.offapp_reviews.map((r) => (
          <div key={r.id} data-testid={`offapp-row-${r.id}`} className="grid gap-4 border-t border-white/5 pt-4 first:border-0 first:pt-0 lg:grid-cols-[1fr_1.4fr]">
            <div className="space-y-1 text-sm">
              <p className="text-amber-100">{r.driver_name} · customer {r.customer_name}</p>
              <p className="text-xs text-zinc-500">Cancelled {fmtDate(r.cancelled_at)} at {r.pickup.address}</p>
              <p className="text-xs text-zinc-500" data-testid={`offapp-points-${r.id}`}>{r.path.length} GPS points{r.watching ? " · still recording" : ""}</p>
              <p className="text-xs text-zinc-600">For human review only — no automatic penalty.</p>
              <div className="flex gap-2 pt-2">
                <button data-testid={`offapp-reviewed-${r.id}`} onClick={() => act(() => api.post(`/admin/offapp-reviews/${r.id}`, { action: "reviewed" }), "Marked reviewed")} className="btn-gold !px-3 !py-1.5 !text-xs">Reviewed</button>
                <button data-testid={`offapp-dismiss-${r.id}`} onClick={() => act(() => api.post(`/admin/offapp-reviews/${r.id}`, { action: "dismissed" }), "Dismissed")} className="btn-outline-gold !px-3 !py-1.5 !text-xs">Dismiss</button>
              </div>
            </div>
            <ReviewMap r={r} />
          </div>
        ))}
      </div>
    </div>
  );
};
